<template>
  <div>
    <div v-if="!pageLoading">
      <v-card flat class="tw-mt-5 md:tw-mt-16">
        <div class="tw-px-4 md:tw-px-8">
          <!-- Header with Gradient -->
          <div class="tw-relative tw-overflow-hidden tw-rounded-xl tw-mb-8"
               style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%);">
            <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-10"></div>
            <div class="tw-relative tw-z-10 tw-text-center tw-py-12 tw-px-6">
              <div class="tw-mb-4">
                <v-icon size="64" color="white" class="tw-mb-2">mdi-account-heart</v-icon>
              </div>
              <h1 class="tw-text-white tw-text-3xl md:tw-text-4xl tw-font-bold tw-mb-2">
                Buying Travel Insurance for Someone Else
              </h1>
              <p class="tw-text-blue-100 tw-text-lg">
                Comprehensive protection for your loved one's journey
              </p>
            </div>
          </div>

          <!-- Buyer vs Policyholder Info Card -->
          <v-card class="tw-mb-8" elevation="4">
            <v-card-text class="tw-p-6">
              <div class="tw-bg-blue-50 tw-rounded-lg tw-p-4 tw-mb-6">
                <div class="tw-flex tw-items-start tw-space-x-3">
                  <v-icon color="blue" size="24">mdi-information</v-icon>
                  <div>
                    <h4 class="tw-font-semibold tw-text-blue-800 tw-mb-1">Important Information</h4>
                    <p class="tw-text-blue-700 tw-text-sm">
                      You are purchasing travel insurance for someone else (18+ years). The traveler will be the policyholder,
                      and you will be the buyer responsible for payment.
                    </p>
                  </div>
                </div>
              </div>

              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                <div class="tw-bg-green-50 tw-rounded-lg tw-p-4">
                  <h5 class="tw-font-semibold tw-text-green-800 tw-mb-2 tw-flex tw-items-center">
                    <v-icon color="green" size="20" class="tw-mr-2">mdi-account</v-icon>
                    You (Buyer)
                  </h5>
                  <p class="tw-text-green-700 tw-text-sm">Responsible for payment and purchase process</p>
                </div>

                <div class="tw-bg-purple-50 tw-rounded-lg tw-p-4">
                  <h5 class="tw-font-semibold tw-text-purple-800 tw-mb-2 tw-flex tw-items-center">
                    <v-icon color="purple" size="20" class="tw-mr-2">mdi-shield-account</v-icon>
                    Traveler (Policyholder)
                  </h5>
                  <p class="tw-text-purple-700 tw-text-sm">The insured person and policy holder</p>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Quote Review Section -->
          <v-card class="tw-mb-8" elevation="8">
            <div class="tw-bg-gradient-to-r tw-from-indigo-500 tw-to-blue-600 tw-p-6">
              <div class="tw-text-center tw-text-white">
                <div class="tw-mb-4">
                  <v-icon size="48" color="white" class="tw-mb-2">mdi-file-document-multiple</v-icon>
                </div>
                <h2 class="tw-text-white tw-text-2xl tw-font-bold tw-mb-2">
                  Quote Review
                </h2>
                <p class="tw-text-blue-100 tw-text-sm">
                  Please review the travel insurance quote details before proceeding with payment
                </p>
              </div>
            </div>

            <v-card-text class="tw-p-8">
              <!-- Quote Summary Cards -->
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-8">
                <v-card class="tw-bg-blue-50 tw-border tw-border-blue-200" flat elevation="2">
                  <v-card-text class="tw-p-6 tw-text-center">
                    <v-icon size="32" color="primary" class="tw-mb-3">mdi-shield-check</v-icon>
                    <div class="tw-text-sm tw-text-gray-600 tw-mb-1">Coverage Type</div>
                    <div class="tw-text-lg tw-font-bold c-blue-text">{{ quote ? (quote.plan_type || 'Individual') : 'Individual' }}</div>
                    <div class="tw-text-sm tw-text-gray-500">{{ quote ? (quote.region || 'Europe') : 'Europe' }}</div>
                  </v-card-text>
                </v-card>

                <v-card class="tw-bg-green-50 tw-border tw-border-green-200" flat elevation="2">
                  <v-card-text class="tw-p-6 tw-text-center">
                    <v-icon size="32" color="success" class="tw-mb-3">mdi-currency-usd</v-icon>
                    <div class="tw-text-sm tw-text-gray-600 tw-mb-1">Premium</div>
                    <div class="tw-text-xl tw-font-bold tw-text-green-600">${{ quote ? quote.premium : 0 }}</div>
                    <div class="tw-text-sm tw-text-gray-500">KSH {{ formatNumber(kshAmount) }}</div>
                  </v-card-text>
                </v-card>

                <v-card class="tw-bg-purple-50 tw-border tw-border-purple-200" flat elevation="2">
                  <v-card-text class="tw-p-6 tw-text-center">
                    <v-icon size="32" color="purple" class="tw-mb-3">mdi-calendar-range</v-icon>
                    <div class="tw-text-sm tw-text-gray-600 tw-mb-1">Duration</div>
                    <div class="tw-text-lg tw-font-bold tw-text-purple-600">{{ calculateDuration(quote?.travel_start_date, quote?.travel_end_date) }} days</div>
                    <div class="tw-text-sm tw-text-gray-500">{{ formatDate(quote?.travel_start_date) }} - {{ formatDate(quote?.travel_end_date) }}</div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Important Notice for Buying Scenario -->
              <v-alert
                type="info"
                variant="tonal"
                class="tw-mb-6"
                prominent
              >
                <template v-slot:prepend>
                  <v-icon>mdi-information-outline</v-icon>
                </template>
                <div>
                  <h4 class="tw-font-semibold tw-mb-2">Review Before Proceeding</h4>
                  <p class="tw-mb-2">
                    You are purchasing travel insurance for <strong>{{ quote ? quote.full_name : 'the traveler' }}</strong>.
                    Please verify all details are correct as this policy cannot be modified after purchase.
                  </p>
                  <ul class="tw-text-sm tw-list-disc tw-list-inside tw-space-y-1">
                    <li>The traveler will receive the policy documents at <strong>{{ quote ? quote.email : 'traveler\'s email' }}</strong></li>
                    <li>You will receive payment confirmation and receipts</li>
                    <li>Both parties will be notified upon policy issuance</li>
                  </ul>
                </div>
              </v-alert>

              <!-- Quote Actions -->
              <div class="tw-flex tw-justify-between tw-items-center tw-pt-4 tw-border-t tw-border-gray-200">
                <div class="tw-text-sm tw-text-gray-600">
                  Quote ID: #{{ quote ? quote.id : 'Loading...' }}
                </div>
                <v-btn
                  color="grey"
                  text
                  small
                  @click="editQuote"
                  :disabled="!quote"
                >
                  <v-icon left size="16">mdi-pencil</v-icon>
                  Edit Quote
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Premium Display Card -->
          <v-card class="tw-mb-8 tw-overflow-hidden" elevation="8"
                  style="background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);">
            <v-card-text class="tw-p-0">
              <div class="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-p-6">
                <div class="tw-text-center">
                  <h2 class="tw-text-white tw-text-2xl tw-font-bold tw-mb-2">
                    Premium Summary
                  </h2>
                  <div class="tw-flex tw-justify-center tw-space-x-2">
                    <v-chip color="white" variant="elevated" size="small">
                      <v-icon start size="16">mdi-account-heart</v-icon>
                      Buying for Someone Else
                    </v-chip>
                    <v-chip color="white" variant="elevated" size="small" v-if="quote">
                      <v-icon start size="16">mdi-airplane-takeoff</v-icon>
                      {{ quote.destination || quote.region || 'International' }}
                    </v-chip>
                  </div>
                </div>
              </div>

              <div class="tw-p-8">
                <v-row align="center" justify="center" class="tw-mb-6">
                  <v-col cols="12" sm="5" class="tw-text-center">
                    <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-lg tw-border tw-border-gray-100">
                      <v-icon size="32" color="success" class="tw-mb-3">mdi-cash-multiple</v-icon>
                      <p class="tw-text-gray-600 tw-text-sm tw-font-medium tw-mb-1">USD Premium</p>
                      <p class="tw-text-3xl tw-font-bold tw-text-gray-800">
                        ${{ quote ? quote.premium : 0 }}
                      </p>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="2" class="tw-text-center tw-hidden sm:tw-block">
                    <v-icon size="40" color="primary">mdi-arrow-right</v-icon>
                  </v-col>

                  <v-col cols="12" sm="5" class="tw-text-center">
                    <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-lg tw-border tw-border-blue-200 tw-border-2">
                      <v-icon size="32" color="primary" class="tw-mb-3">mdi-currency-usd</v-icon>
                      <p class="tw-text-gray-600 tw-text-sm tw-font-medium tw-mb-1">KES Amount</p>
                      <p class="tw-text-3xl tw-font-bold tw-text-blue-700">
                        KES {{ formatNumber(kshAmount) }}
                      </p>
                      <p class="tw-text-xs tw-text-gray-500 tw-mt-1">
                        Rate: 1 USD = {{ exchangeRate }} KES
                      </p>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>

          <!-- Quote Summary Cards -->
          <v-row class="tw-mb-8">
            <!-- Travel Details Card -->
            <v-col cols="12" md="6">
              <v-card class="tw-h-full tw-overflow-hidden" elevation="8">
                <div class="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-p-6">
                  <div class="tw-flex tw-items-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                      <v-icon size="32" color="white">mdi-airplane-takeoff</v-icon>
                    </div>
                    <div>
                      <h3 class="tw-text-xl tw-font-bold tw-mb-1">Travel Details</h3>
                      <p class="tw-text-blue-100 tw-text-sm">Journey information</p>
                    </div>
                  </div>
                </div>

                <v-card-text class="tw-p-8" v-if="quote">
                  <div class="tw-space-y-6">
                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="blue" size="20" class="tw-mr-3">mdi-map-marker</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Destination</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ quote.destination || quote.region }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="green" size="20" class="tw-mr-3">mdi-calendar-start</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Departure</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ formatDate(quote.travel_start_date) }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="red" size="20" class="tw-mr-3">mdi-calendar-end</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Return</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ formatDate(quote.travel_end_date) }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="purple" size="20" class="tw-mr-3">mdi-clock-outline</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Duration</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ calculateDuration(quote.travel_start_date, quote.travel_end_date) }} days</span>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Traveler Details Card -->
            <v-col cols="12" md="6">
              <v-card class="tw-h-full tw-overflow-hidden" elevation="8">
                <div class="tw-bg-gradient-to-r tw-from-purple-500 tw-to-purple-600 tw-p-6">
                  <div class="tw-flex tw-items-center tw-text-white">
                    <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-3 tw-mr-4">
                      <v-icon size="32" color="white">mdi-account</v-icon>
                    </div>
                    <div>
                      <h3 class="tw-text-xl tw-font-bold tw-mb-1">Traveler Details</h3>
                      <p class="tw-text-purple-100 tw-text-sm">Policyholder information</p>
                    </div>
                  </div>
                </div>

                <v-card-text class="tw-p-8" v-if="quote">
                  <div class="tw-space-y-6">
                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="purple" size="20" class="tw-mr-3">mdi-account</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Full Name</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ quote.full_name }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="blue" size="20" class="tw-mr-3">mdi-email</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Email</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ quote.email }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-100">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="green" size="20" class="tw-mr-3">mdi-calendar</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Date of Birth</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ formatDate(quote.date_of_birth) }}</span>
                      </div>
                    </div>

                    <div class="tw-group">
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <div class="tw-flex tw-items-center">
                          <v-icon color="orange" size="20" class="tw-mr-3">mdi-card-account-details</v-icon>
                          <span class="tw-font-medium tw-text-gray-700">Age</span>
                        </div>
                        <span class="tw-text-gray-800 tw-font-semibold">{{ calculateAge(quote.date_of_birth) }} years</span>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Buyer Information Form -->
          <v-card class="tw-mb-8" elevation="4">
            <div class="tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-p-6">
              <h3 class="tw-text-white tw-text-xl tw-font-bold tw-mb-1">Your Information (Buyer)</h3>
              <p class="tw-text-green-100 tw-text-sm">Your details for payment and contact purposes</p>
            </div>

            <v-card-text class="tw-p-6">
              <v-form ref="buyerForm" v-model="buyerFormValid">
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <v-text-field
                    v-model="buyerDetails.firstName"
                    label="First Name"
                    :rules="[rules.required]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-account"
                  ></v-text-field>

                  <v-text-field
                    v-model="buyerDetails.lastName"
                    label="Last Name"
                    :rules="[rules.required]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-account"
                  ></v-text-field>

                  <v-text-field
                    v-model="buyerDetails.email"
                    label="Email Address"
                    type="email"
                    :rules="[rules.required, rules.email]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-email"
                  ></v-text-field>

                  <v-text-field
                    v-model="buyerDetails.phone"
                    label="Phone Number"
                    :rules="[rules.required, rules.phone]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-phone"
                  ></v-text-field>
                </div>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Payment Section -->
          <v-card class="tw-mb-8" elevation="4">
            <div class="tw-bg-gradient-to-r tw-from-emerald-500 tw-to-green-600 tw-p-6">
              <h3 class="tw-text-white tw-text-xl tw-font-bold tw-mb-1">Payment</h3>
              <p class="tw-text-green-100 tw-text-sm">Secure payment processing</p>
            </div>

            <v-card-text class="tw-p-6">
              <div class="tw-text-center tw-mb-6">
                <div class="tw-bg-white tw-rounded-xl tw-p-6 tw-shadow-sm tw-border tw-border-gray-100 tw-max-w-md tw-mx-auto">
                  <p class="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-3">Total Amount</p>
                  <div class="tw-text-4xl tw-font-bold tw-text-emerald-600 tw-mb-2">
                    KSH {{ formatNumber(kshAmount) }}
                  </div>
                  <div class="tw-text-sm tw-text-gray-600 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-inline-block">
                    USD {{ quote ? quote.premium : 0 }} @ {{ this.exchangeRate }} KES
                  </div>
                </div>
              </div>

              <!-- Payment Methods -->
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-mb-6">
                <!-- M-Pesa Card -->
                <v-card
                  class="tw-cursor-pointer tw-transition-all hover:tw-shadow-xl hover:tw--translate-y-1"
                  :class="{ 'tw-ring-4 tw-ring-green-300 tw-shadow-xl': selectedPaymentMethod === 'mpesa' }"
                  @click="selectPaymentMethod('mpesa')"
                  elevation="4"
                >
                  <v-card-text class="tw-p-6 tw-text-center">
                    <div class="tw-mb-4">
                      <img src="/img/mpesa-logo.png" alt="M-Pesa" class="tw-h-12 tw-mx-auto tw-mb-3" />
                      <h4 class="tw-font-bold tw-text-green-700">M-Pesa</h4>
                    </div>
                    <p class="tw-text-sm tw-text-gray-600">Pay securely with your M-Pesa account</p>
                  </v-card-text>
                </v-card>

                <!-- Card Payment -->
                <v-card
                  class="tw-cursor-pointer tw-transition-all hover:tw-shadow-xl hover:tw--translate-y-1"
                  :class="{ 'tw-ring-4 tw-ring-blue-300 tw-shadow-xl': selectedPaymentMethod === 'card' }"
                  @click="selectPaymentMethod('card')"
                  elevation="4"
                >
                  <v-card-text class="tw-p-6 tw-text-center">
                    <div class="tw-mb-4">
                      <v-icon size="48" color="blue" class="tw-mb-3">mdi-credit-card</v-icon>
                      <h4 class="tw-font-bold tw-text-blue-700">Card Payment</h4>
                    </div>
                    <p class="tw-text-sm tw-text-gray-600">Pay with Visa, Mastercard, or Debit Card</p>
                  </v-card-text>
                </v-card>
              </div>

              <!-- M-Pesa Input -->
              <div v-if="selectedPaymentMethod === 'mpesa'" class="tw-mt-6">
                <v-text-field
                  v-model="mpesaNumber"
                  label="M-Pesa Phone Number"
                  placeholder="254XXXXXXXXX"
                  outlined
                  :rules="[rules.required, rules.mpesaPhone]"
                  prepend-inner-icon="mdi-cellphone"
                ></v-text-field>
              </div>
            </v-card-text>
          </v-card>

          <!-- Payment Processing Status -->
          <v-card v-if="processingPayment" class="tw-mb-8" elevation="4">
            <v-card-text class="tw-p-8 tw-text-center">
              <v-progress-circular size="60" indeterminate color="primary" class="tw-mb-4"></v-progress-circular>
              <h3 class="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-2">Processing Payment</h3>
              <p class="tw-text-gray-600 tw-mb-4">
                Please wait while we process your payment and issue the travel insurance policy.
              </p>
              <div class="tw-bg-blue-50 tw-rounded-lg tw-p-4">
                <p class="tw-text-sm tw-text-blue-700">
                  <v-icon color="blue" size="16" class="tw-mr-1">mdi-information</v-icon>
                  Policy will be issued to: <strong>{{ quote ? quote.full_name : 'Traveler' }}</strong>
                  <br>
                  Payment notifications will be sent to: <strong>{{ buyerDetails.email }}</strong>
                </p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <div class="tw-flex tw-justify-between tw-items-center tw-mb-8">
            <v-btn
              color="grey"
              outlined
              large
              @click="goBack"
              :disabled="processingPayment"
              class="tw-px-8"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Go Back
            </v-btn>

            <v-btn
              color="primary"
              large
              :disabled="!canProceedToPayment || processingPayment"
              :loading="processingPayment"
              @click="processPayment"
              class="tw-px-8"
            >
              <v-icon left>mdi-shield-check</v-icon>
              {{ processingPayment ? 'Processing...' : 'Secure Payment' }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Loading Overlay -->
    <div v-if="pageLoading" class="tw-flex tw-justify-center tw-items-center tw-h-screen">
      <v-progress-circular size="50" indeterminate color="primary"></v-progress-circular>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { TravelPolicyService } from '@/services/TravelPolicyService';
import { travelPaymentReference } from '@/utils/travelPaymentReference';
import exchangeRates from '@/config/exchangeRates';

export default {
  name: 'BuyForSomeoneElseCover',
  data() {
    return {
      pageLoading: false,
      quote: null,
      exchangeRate: exchangeRates.USD_TO_KSH, // Default exchange rate from config
      selectedPaymentMethod: null,
      mpesaNumber: '',
      processingPayment: false,
      buyerFormValid: false,

      // Buyer details
      buyerDetails: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },

      // Validation rules
      rules: {
        required: (v) => !!v || 'This field is required',
        email: (v) => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(v) || 'Enter a valid email address';
        },
        phone: (v) => {
          const pattern = /^(\+254|254|0)?[7-9]\d{8}$/;
          return pattern.test(v) || 'Enter a valid phone number';
        },
        mpesaPhone: (v) => {
          const pattern = /^(254)?[7-9]\d{8}$/;
          return pattern.test(v) || 'Enter a valid M-Pesa number (254XXXXXXXXX)';
        },
      },
    };
  },

  computed: {
    kshAmount() {
      return this.quote ? Math.round(this.quote.premium * this.exchangeRate) : 0;
    },

    canProceedToPayment() {
      return this.buyerFormValid
             && this.selectedPaymentMethod
             && (this.selectedPaymentMethod !== 'mpesa' || this.mpesaNumber);
    },
  },

  async mounted() {
    await this.loadQuoteData();
    await this.fetchExchangeRate();
  },

  methods: {
    async loadQuoteData() {
      try {
        this.pageLoading = true;
        const quoteId = this.$route.query.q;

        if (!quoteId) {
          this.$router.push({ name: 'StartTravel' });
          return;
        }

        // Get quote from store
        this.quote = this.$store.getters['rfq/getTravelQuoteResponse'];

        if (!this.quote) {
          throw new Error('Quote not found');
        }
      } catch (error) {
        // console.error('Error loading quote:', error);
        this.$toast.error('Error loading quote data', 'Error');
        this.$router.push({ name: 'StartTravel' });
      } finally {
        this.pageLoading = false;
      }
    },

    async fetchExchangeRate() {
      this.exchangeRate = exchangeRates.USD_TO_KSH;
    },

    calculateAge(dateOfBirth) {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }

      return age;
    },

    calculateDuration(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    formatDate(dateString) {
      if (!dateString) return 'Not specified';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    selectPaymentMethod(method) {
      this.selectedPaymentMethod = method;
      if (method !== 'mpesa') {
        this.mpesaNumber = '';
      }
    },

    async processPayment() {
      try {
        this.processingPayment = true;

        // Validate forms
        if (!this.$refs.buyerForm.validate()) {
          this.$toast.error('Please fix validation errors', 'Validation Error');
          return;
        }

        // Create enhanced travel context with buyer and policyholder separation
        const enhancedContext = {
          ...this.quote,
          buyingForSomeoneElse: true,

          // Buyer information (for payment and contact)
          buyerFirstName: this.buyerDetails.firstName,
          buyerLastName: this.buyerDetails.lastName,
          buyerEmail: this.buyerDetails.email,
          buyerPhone: this.buyerDetails.phone,

          // Traveler information (already from quote - is the policyholder)
          insuredFirstName: this.quote.full_name?.split(' ')[0] || 'Unknown',
          insuredSurname: this.quote.full_name?.split(' ').slice(1).join(' ') || 'Unknown',
          // insuredFirstName: 'TEST',
          // insuredSurname: 'TEST',
          insuredDateOfBirth: this.quote.date_of_birth,
          insuredPassport: this.quote.passport_no || 'UNKNOWN',

          // Policyholder is the same as insured for buying for someone else
          policyholderFirstName: this.quote.full_name?.split(' ')[0] || 'Unknown',
          policyholderSurname: this.quote.full_name?.split(' ').slice(1).join(' ') || 'Unknown',
          policyholderDateOfBirth: this.quote.date_of_birth,
          policyholderPassport: this.quote.passport_no || 'UNKNOWN',

          // Travel details
          selectedCountry: this.quote.selectedCountry || this.quote.destination_country,
          destination: this.quote.destination || this.quote.region,

          // Contact details for policy (use traveler's email, buyer's for notifications)
          email: this.quote.email, // Traveler's email for policy
          phone_number: this.buyerDetails.phone, // Buyer's phone for payment notifications
        };

        // Set travel context for policy issuance
        TravelPolicyService.setTravelContext(this.$store, enhancedContext);

        // Create payment context
        const paymentContext = {
          productId: this.quote.id,
          payableAmount: this.kshAmount,
          email: this.quote.email || this.buyerDetails.email,
          phoneNumber: this.formData.mobile_main || this.buyerDetails.phone,
          transactionReference: travelPaymentReference(this.form?.passport_number || this.form?.id_number || this.form?.id_no),
          transactionDescription: `TV_rfq-${this.quote.id}`,
          serviceType: 'TRAVEL',
          transactionType: 'PAYMENT',
          buyingForSomeoneElse: true,
          buyerDetails: this.buyerDetails,
          travelerDetails: {
            name: this.quote.full_name,
            email: this.quote.email,
            dateOfBirth: this.quote.date_of_birth,
          },
        };

        // Store payment context
        this.$store.commit('payment/setPaymentContext', paymentContext);

        // console.log('Processing payment for someone else with context:', paymentContext);

        // For buying for someone else, handle payment and policy issuance directly
        if (this.selectedPaymentMethod === 'mpesa') {
          await this.processMpesaPayment(paymentContext);
        } else {
          // For card payments, navigate to payment processing
          this.$router.push({
            name: 'PaymentProcessing',
            query: {
              type: 'travel',
              method: this.selectedPaymentMethod,
              amount: this.kshAmount,
              reference: paymentContext.transactionReference,
              buyingForSomeoneElse: true,
            },
          });
        }
      } catch (error) {
        // console.error('Payment processing error:', error);
        this.$toast.error('Payment processing failed. Please try again.', 'Error');
      } finally {
        this.processingPayment = false;
      }
    },

    formatNumber(num) {
      if (!num) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    async processMpesaPayment(paymentContext) {
      try {
        // console.log('Processing M-Pesa payment for someone else scenario');

        // Show payment processing state
        this.processingPayment = true;
        this.$toast.info('Processing M-Pesa payment...', 'Payment');

        // Simulate M-Pesa payment request (replace with actual M-Pesa integration)
        const mpesaResponse = await axios.post('/customer/payments/mpesa/stk-push', {
          phoneNumber: this.mpesaNumber,
          amount: this.kshAmount,
          reference: paymentContext.transactionReference,
          description: `Travel Insurance for ${this.quote.full_name}`,
        });

        if (mpesaResponse.data.success) {
          // Start polling for payment status
          await this.pollPaymentStatus(paymentContext.transactionReference, paymentContext);
        } else {
          throw new Error(mpesaResponse.data.message || 'M-Pesa payment initiation failed');
        }
      } catch (error) {
        // console.error('M-Pesa payment error:', error);
        this.$toast.error('M-Pesa payment failed. Please try again.', 'Payment Error');
        this.processingPayment = false;
      }
    },

    async pollPaymentStatus(transactionReference, paymentContext) {
      let attempts = 0;
      const maxAttempts = 30; // 5 minutes of polling

      const poll = async () => {
        try {
          const response = await axios.get(`/customer/payments/status/${transactionReference}`);
          const { status } = response.data;

          if (status === 'COMPLETED' || status === 'SUCCESS') {
            this.$toast.success('Payment completed successfully!', 'Success');

            // Issue the travel policy after successful payment
            await this.issueTravelPolicyForBuyingScenario(paymentContext);
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            this.$toast.error('Payment was cancelled or failed. Please try again.', 'Payment Failed');
            this.processingPayment = false;
          } else if (attempts < maxAttempts) {
            // Continue polling
            attempts += 1;
            setTimeout(poll, 10000); // Poll every 10 seconds
          } else {
            // Timeout
            this.$toast.warning('Payment status check timed out. Please contact support if payment was deducted.', 'Timeout');
            this.processingPayment = false;
          }
        } catch (error) {
          // console.error('Payment status polling error:', error);
          attempts += 1;

          if (attempts < maxAttempts) {
            setTimeout(poll, 10000);
          } else {
            this.$toast.error('Unable to verify payment status. Please contact support.', 'Error');
            this.processingPayment = false;
          }
        }
      };

      // Start polling after 5 seconds
      setTimeout(poll, 5000);
    },

    async issueTravelPolicyForBuyingScenario(paymentContext) {
      try {
        // console.log('Issuing travel policy for buying for someone else scenario...');

        // Create enhanced payment context with buyer and policyholder separation
        const enhancedPaymentContext = {
          ...paymentContext,
          productId: this.quote.id,
          payableAmount: this.kshAmount,

          // Use buyer's email for payment notifications
          email: this.buyerDetails.email,
          phoneNumber: this.buyerDetails.phone,

          // Specify this is a buying for someone else scenario
          buyingForSomeoneElse: true,
          buyerInformation: {
            firstName: this.buyerDetails.firstName,
            lastName: this.buyerDetails.lastName,
            email: this.buyerDetails.email,
            phone: this.buyerDetails.phone,
          },

          // Traveler/Policyholder information (from quote)
          policyholderInformation: {
            firstName: this.quote.full_name?.split(' ')[0] || 'Unknown',
            lastName: this.quote.full_name?.split(' ').slice(1).join(' ') || 'Unknown',
            email: this.quote.email,
            dateOfBirth: this.quote.date_of_birth,
            passport: this.quote.passport_no,
          },
        };

        // console.log('Enhanced payment context for policy issuance:', enhancedPaymentContext);

        // Issue the policy using the TravelPolicyService
        const result = await TravelPolicyService.issueTravelPolicy(enhancedPaymentContext, this.$store);

        // Show success message
        this.$toast.success(
          `Policy ${result.policyNumber} issued successfully! Policy details have been sent to ${this.quote.email} (traveler) and ${this.buyerDetails.email} (buyer).`,
          'Policy Issued',
          { timeout: 10000 },
        );

        // console.log('Travel policy issued successfully for buying scenario:', result);

        // Redirect to success page after short delay
        setTimeout(() => {
          this.$router.push({
            name: 'TravelPolicySuccess',
            query: {
              policyNumber: result.policyNumber,
              buyingForSomeoneElse: true,
              travelerName: this.quote.full_name,
              buyerName: `${this.buyerDetails.firstName} ${this.buyerDetails.lastName}`,
            },
          });
        }, 3000);
      } catch (error) {
        // console.error('Policy issuance error:', error);
        this.$toast.error(
          'Payment was successful, but there was an issue issuing the policy. Our team has been notified and will process your policy manually.',
          'Policy Processing',
          { timeout: 10000 },
        );
      } finally {
        this.processingPayment = false;
      }
    },

    editQuote() {
      // Navigate back to StartTravel to edit the quote
      this.$router.push({
        name: 'StartTravel',
        query: { edit: this.quote?.id },
      });
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.tw-ring-4 {
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
}

.tw-ring-green-300 {
  --tw-ring-color: rgb(134 239 172);
}

.tw-ring-blue-300 {
  --tw-ring-color: rgb(147 197 253);
}

.hover\:tw--translate-y-1:hover {
  transform: translateY(-0.25rem);
}
</style>
